import './OfferCommentFormStyle.less';

import { Icon, RadioButtonGroupInput, SingleLineInput, ValidationInput } from '@opendoor/bricks';
import React from 'react';
import { OfferedByType } from './OfferDetailPage';

export interface IOfferCommentFormProps {
  agentComment: string;
  agentEmail: string;
  agentFirstName: string;
  agentLastName: string;
  agentPhoneNumber: string;
  agentMlsId: string;
  agentMlsOfficeId: string;
  agentMlsLicenseNumber: string;
  offeredByType: OfferedByType;
  onAgentCommentChange: (agentComment: string) => void;
  onAgentEmailChange: (newValue: string, isValid: boolean) => void;
  onAgentFirstNameChange: (newValue: string) => void;
  onAgentLastNameChange: (newValue: string) => void;
  onAgentPhoneNumberChange: (newValue: string, isValid: boolean) => void;
  onAgentMlsIdChange: (newValue: string) => void;
  onAgentMlsOfficeIdChange: (newValue: string) => void;
  onAgentMlsLicenseNumberChange: (newValue: string) => void;
  onOfferedByTypeChange: (newValue: boolean) => void;
  shouldShowLicenseNumberInput: () => boolean;
  shouldShowOfficeIdInput: () => boolean;
  shouldShowMlsIdInput: () => boolean;
  hasBeenSubmitted: boolean;
}

export default class OfferCommentForm extends React.PureComponent<IOfferCommentFormProps> {
  public static defaultProps: Partial<IOfferCommentFormProps> = {
    agentComment: '',
    agentEmail: '',
  };

  private onAgentFirstNameChange = this.getOnChangeHandler(this.props.onAgentFirstNameChange);
  private onAgentLastNameChange = this.getOnChangeHandler(this.props.onAgentLastNameChange);
  private onAgentEmailChange = this.getOnChangeHandler(this.props.onAgentEmailChange);
  private onAgentPhoneNumberChange = this.getOnChangeHandler(this.props.onAgentPhoneNumberChange);
  private onAgentMlsIdChange = this.getOnChangeHandler(this.props.onAgentMlsIdChange);
  private onAgentMlsOfficeIdChange = this.getOnChangeHandler(this.props.onAgentMlsOfficeIdChange);
  private onAgentMlsLicenseNumberChange = this.getOnChangeHandler(
    this.props.onAgentMlsLicenseNumberChange,
  );

  public render(): JSX.Element {
    const {
      agentComment,
      agentEmail,
      agentFirstName,
      agentLastName,
      agentPhoneNumber,
      onAgentCommentChange,
      hasBeenSubmitted,
      agentMlsId,
      agentMlsOfficeId,
      agentMlsLicenseNumber,
      offeredByType,
      shouldShowLicenseNumberInput,
      shouldShowOfficeIdInput,
      shouldShowMlsIdInput,
    } = this.props;

    return (
      <div className="od-offer-comment-form od-base-box box no-padding">
        <div className="top-section less-padding">
          <section className="row header">
            <div className="col-xs-1">
              <Icon className="right-arrow" name="arrow-right-square" />
            </div>
            <div className="col-xs-10">
              <h6>Last Step: Is there anything else you’d like to share about this offer?</h6>
            </div>
          </section>
          <section className="row body-section">
            <div className="col-xs-offset-1 col-xs-10">
              <textarea
                onChange={e => onAgentCommentChange(e.target.value)}
                placeholder="This can be about the home, your clients, your offer, or anything else."
                value={agentComment}
              />
              <div className="question">
                <SingleLineInput
                  content={agentFirstName}
                  name="agentFirstName"
                  onChange={this.onAgentFirstNameChange}
                  placeholder="Jane"
                  title="Your first name"
                  subtitle="* Required"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <div className="question">
                <SingleLineInput
                  content={agentLastName}
                  name="agentLastName"
                  onChange={this.onAgentLastNameChange}
                  placeholder="Smith"
                  title="Your last name"
                  subtitle="* Required"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <div className="question">
                <ValidationInput
                  content={agentEmail}
                  inputType="email"
                  name="agentEmail"
                  onChange={this.onAgentEmailChange}
                  title="Your email"
                  subtitle="* Required"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <div className="question">
                <ValidationInput
                  content={agentPhoneNumber}
                  inputType="phone"
                  name="agentPhoneNumber"
                  onChange={this.onAgentPhoneNumberChange}
                  placeholder="(555) 555-5555"
                  title="Your phone"
                  subtitle="* Required"
                  assertNotNull={hasBeenSubmitted}
                />
              </div>
              <p className="prose">
                <i>
                  By clicking "Submit Offer" I consent to receive marketing calls and texts from
                  Opendoor and its affiliates to the number provided including by automated means. I
                  may opt out at any time and consent is not a condition of purchase.
                </i>
              </p>
              <div className="question">
                <RadioButtonGroupInput
                  onChange={this.props.onOfferedByTypeChange}
                  options={[
                    { label: 'MLS Agent', inputName: 'MLS Agent', value: 'offered_by_mls_agent' },
                    {
                      inputName: 'Non-MLS Agent',
                      label: 'Non-MLS Agent',
                      value: 'offered_by_non_mls_agent',
                    },
                  ]}
                  selectedValue={offeredByType}
                  title={'Agent type'}
                />
              </div>
              {shouldShowMlsIdInput() && (
                <div className="question">
                  <SingleLineInput
                    content={agentMlsId}
                    name="agentMlsID"
                    onChange={this.onAgentMlsIdChange}
                    title={'Your agent MLS ID'}
                    subtitle="* Required"
                    assertNotNull={hasBeenSubmitted && shouldShowMlsIdInput()}
                  />
                </div>
              )}
              {shouldShowOfficeIdInput() && (
                <div className="question">
                  <SingleLineInput
                    content={agentMlsOfficeId}
                    name="agentMlsOfficeId"
                    subtitle="* Required"
                    onChange={this.onAgentMlsOfficeIdChange}
                    title={'Your MLS office ID'}
                    assertNotNull={hasBeenSubmitted && shouldShowOfficeIdInput()}
                  />
                </div>
              )}
              {shouldShowLicenseNumberInput() && (
                <div className="question">
                  <SingleLineInput
                    content={agentMlsLicenseNumber}
                    name="agentMlsLicenseNumber"
                    onChange={this.onAgentMlsLicenseNumberChange}
                    subtitle="* Required"
                    title={'Your agent license number'}
                    assertNotNull={hasBeenSubmitted && shouldShowLicenseNumberInput()}
                  />
                </div>
              )}
            </div>
          </section>
        </div>
        <div className="divider" />
        <div className="row bottom-section less-padding">
          <div className="col-xs-offset-1 col-xs-10">
            <p className="prose">
              <i>
                By submitting this offer, I understand that the title company and home warranty
                provider associated with this transaction may contact me directly to coordinate
                closing details and warranty activation.
              </i>
            </p>
            <p className="prose bottom-text">
              Be sure to double-check your information before you submit.
            </p>
          </div>
        </div>
      </div>
    );
  }

  private getOnChangeHandler(cb) {
    return (name, ...args) => cb.apply(null, args);
  }
}
